import { useState } from '#imports'

const REGISTRY_KEY = 'shopping-assistant-structured-data-registry'

/**
 * Structured data captured from prepareJsonLdScript calls for the current
 * page, keyed by schema.org @type. A page-scoped useState so it survives
 * client-side navigation within the same app instance without leaking across
 * SSR requests.
 */
export function useStructuredDataRegistry() {
  return useState<Record<string, object>>(REGISTRY_KEY, () => ({}))
}

function getTypeKey(data: Record<string, unknown>): string {
  const type = data['@type']

  return typeof type === 'string' ? type : 'unknown'
}

/**
 * Records a JSON-LD object under its @type, replacing any previous object of
 * the same type (handles a reactive useHead getter re-firing as its own async
 * data resolves) while keeping distinct types side by side (e.g. a page
 * declaring both Organization and WebSite).
 */
export function registerStructuredData(data: Record<string, unknown>) {
  if (!import.meta.client) return

  const registry = useStructuredDataRegistry()
  registry.value = { ...registry.value, [getTypeKey(data)]: data }
}

/**
 * Clears the registry. Called at the start of each navigation so a
 * slow-to-declare page never inherits the previous page's structured data.
 */
export function resetStructuredDataRegistry() {
  if (!import.meta.client) return

  useStructuredDataRegistry().value = {}
}
